class GorgiasExtensions extends HTMLElement {
  settings: any[] = [];
  breakpoint = 990;

  connectedCallback() {
    try {
      this.settings = JSON.parse(this.getAttribute("data-settings") || "[]");
      console.log("[Gorgias Extensions] Settings loaded:", this.settings);

      this.initGorgiasExtensions();
    } catch (error) {
      console.error("[Gorgias Extensions] Error initializing:", error);
    }
  }

  initGorgiasExtensions() {
    const initGorgiasChatPromise = window.GorgiasChat
      ? window.GorgiasChat.init()
      : new Promise<void>((resolve) => {
          window.addEventListener("gorgias-widget-loaded", () => resolve());
        });

    initGorgiasChatPromise.then(async () => {
      console.log("[Gorgias Extensions] Initialized.");

      // Initialize workflow overwrite functionality
      this.initWorkflowOverwrite();

      // Future extensions can be initialized here
    });
  }

  initWorkflowOverwrite() {
    let chatDocument: Document | null | undefined = null;

    const onClickHandler = (event: MouseEvent) => {
      console.log("[Gorgias Extensions] Click event detected in chat window.");
      const workflows = chatDocument?.querySelectorAll('[data-testid="workflows"] > div');

      if (!workflows) return;

      for (let i = 0; i < workflows.length; i++) {
        const workflowButton = workflows[i];
        if (workflowButton.contains(event.target as Node)) {
          const workflowLabel = workflowButton.querySelector("h2")?.textContent;

          if (workflowLabel) {
            const matchingOverwrite = this.findMatchingWorkflowOverwrite(workflowLabel);

            if (matchingOverwrite) {
              event.stopPropagation();
              event.preventDefault();

              console.log(`[Gorgias Extensions] Redirecting from "${workflowLabel}" to ${matchingOverwrite.link_url}`);
              
              if (matchingOverwrite.open_in_new_tab) {
                // Open in new tab - use parent window context to avoid iOS Safari issues
                const parentWindow = window.parent || window;
                parentWindow.open(matchingOverwrite.link_url, '_blank', 'noopener,noreferrer');
              } else {
                // Close chat window first, then redirect in current tab
                window.GorgiasChat.close();
                // Use parent window for navigation as well
                const parentWindow = window.parent || window;
                parentWindow.location.href = matchingOverwrite.link_url;
              }
            }
          }
        }
      }
    };

    window.GorgiasChat.on("widget:opened", () => {
      console.log("[Gorgias Extensions] Gorgias chat opened.");

      // Small delay to ensure iframe is in DOM
      setTimeout(() => {
        const chatFrame = document.getElementById("chat-window") as HTMLIFrameElement | null;
        
        if (!chatFrame) {
          console.warn("[Gorgias Extensions] Could not find the chat window iframe.");
          return;
        }

        const setupEventListeners = () => {
          chatDocument = this.getIframeDocument(chatFrame);

          if (!chatDocument) {
            console.warn("[Gorgias Extensions] Could not access chat window document.");
            return;
          }

          console.log("[Gorgias Extensions] Successfully accessed chat iframe document.");

          // Remove any existing listeners before adding new ones
          chatDocument.removeEventListener("click", onClickHandler as EventListener, true);
          chatDocument.addEventListener("click", onClickHandler as EventListener, true);
        };

        // If iframe is already loaded, set up immediately
        if (chatFrame.contentDocument || (chatFrame.contentWindow && chatFrame.contentWindow.document)) {
          setupEventListeners();
        } else {
          // Otherwise wait for load event
          chatFrame.addEventListener("load", setupEventListeners, { once: true });
        }
      }, 100);
    });
  }

  /**
   * Get iframe document with fallback methods
   */
  getIframeDocument(iframe: HTMLIFrameElement): Document | null {
    try {
      // Method 1: Standard contentDocument
      if (iframe.contentDocument) {
        return iframe.contentDocument;
      }

      // Method 2: contentWindow.document fallback
      if (iframe.contentWindow && iframe.contentWindow.document) {
        return iframe.contentWindow.document;
      }

      return null;
    } catch (error) {
      console.error("[Gorgias Extensions] Error accessing iframe document:", error);
      return null;
    }
  }

  findMatchingWorkflowOverwrite(workflowLabel: string) {
    if (!workflowLabel) return null;

    const normalizedLabel = workflowLabel.trim().toLowerCase();

    // Check if we're on mobile for mobile_only setting
    const isMobile = window.matchMedia(`(max-width: ${this.breakpoint}px)`).matches;

    // Filter blocks to only workflow_overwrite type
    const workflowOverwrites = this.settings.filter((block) => block.type === "workflow_overwrite" && block.settings);

    for (const block of workflowOverwrites) {
      const blockSettings = block.settings;
      const blockLabel = blockSettings.workflow_label?.trim().toLowerCase();

      // Skip if this is a mobile-only block and we're not on mobile
      if (blockSettings.mobile_only && !isMobile) continue;

      if (blockLabel && blockLabel === normalizedLabel && blockSettings.link_url) {
        return {
          workflow_label: blockSettings.workflow_label,
          link_url: blockSettings.link_url,
          open_in_new_tab: blockSettings.open_in_new_tab
        };
      }
    }

    return null;
  }
}

customElements.define("gorgias-extensions", GorgiasExtensions);
